'use client';

import * as RadixForm from '@radix-ui/react-form';
import { twMerge } from '@tui-cruises/mein-schiff-web-tailwind-config';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { InputFieldError } from './InputFieldError';
import { InputFieldInput } from './InputFieldInput';
import { InputFieldLabel } from './InputFieldLabel';
import { InputFieldNote } from './InputFieldNote';

import type { InputFieldInputProps } from './InputFieldInput';
import type {
  ForwardRefRenderFunction,
  ReactNode} from 'react';

export type InputFieldProps = Omit<InputFieldInputProps, 'required'> & {
  label?: ReactNode;
  note?: ReactNode;
  error?: string;

  /**
   * Use `label` if you don't want to add the `required` attribute to the input element itself.
   * This will avoid highlighting the input as invalid, when there is no value.
   * This is usually a user friendlier way than highlighting the field invalid on initial page load.
   */
  required?: boolean | 'label';
  asRadix?: boolean;
  radixFormMessages?: RadixForm.FormMessageProps[];
};

const InputFieldRenderFunction: ForwardRefRenderFunction<
  HTMLInputElement,
  InputFieldProps
> = (props, forwardedRef) => {
  const {
    label,
    note,
    error,
    className,
    asRadix,
    radixFormMessages,
    required,
    ...attrs
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    forwardedRef,
    () => inputRef.current,
  );

  useEffect(() => {
    if (asRadix) {
      return;
    }

    inputRef.current?.setCustomValidity(error ?? '');
  }, [asRadix, error]);

  if (!asRadix) {
    return (
      <div className={twMerge('block space-y-1', className)}>
        <label className="block space-y-1">
          {label && (
            <InputFieldLabel asChild>
              <div>
                {label}
                {required ? '*' : ''}
              </div>
            </InputFieldLabel>
          )}
          <InputFieldInput
            {...attrs}
            required={typeof required === 'boolean' ? required : undefined}
            ref={inputRef}
          />
          {!error && note && <InputFieldNote>{note}</InputFieldNote>}
        </label>

        {error && <InputFieldError>{error}</InputFieldError>}
      </div>
    );
  }

  return (
    <RadixForm.Field name={attrs.name ?? ''} className={className} asChild>
      <div className="space-y-1">
        <label className="block space-y-1">
          <RadixForm.Label asChild>
            <InputFieldLabel asChild>
              <div>
                {label}
                {required ? '*' : ''}
              </div>
            </InputFieldLabel>
          </RadixForm.Label>
          <RadixForm.Control asChild>
            <InputFieldInput
              {...attrs}
              required={typeof required === 'boolean' ? required : undefined}
            />
          </RadixForm.Control>
          <RadixForm.ValidityState>
            {validity =>
              (validity?.valid ?? true) && (
                <InputFieldNote>{note}</InputFieldNote>
              )
            }
          </RadixForm.ValidityState>
        </label>

        {(radixFormMessages ?? []).map((props, idx) => (
          <RadixForm.Message key={idx} {...props} asChild>
            <InputFieldError>{props.children}</InputFieldError>
          </RadixForm.Message>
        ))}
      </div>
    </RadixForm.Field>
  );
};

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  InputFieldRenderFunction,
);
